import {
  FH_FIELD,
  FH_FLAG,
  FH_PREDICATE_COUNTRIES_KEY,
  FH_PREDICATE_KEY_ALIASES,
  FH_ROLE,
  FH_WARN,
  FH_YES_NO,
} from './company-form.constants';
import {
  CompanyFieldDescriptor,
  CompanyFormSection,
  FhPredicate,
  FhPredicateContext,
} from './company-form.types';

// Evaluates the `visible_when` / `required_when` blocks from companies-form.config.json.
//
// Three predicate shapes are supported, and every entry in a block is AND-ed:
//   { "countries": ["CO", "MX"] }          -> matches the session country
//   { "international_company": "yes" }     -> another field equals a value
//   { "type_of_company": ["agency"] }      -> another field is one of a set
//
// Pure functions, no Angular imports — the orchestrator owns the form, this owns the semantics.

// Unknown predicate keys are warned about once per key, not once per change detection pass.
const warnedKeys = new Set<string>();

export function evaluatePredicate(
  predicate: FhPredicate | null | undefined,
  ctx: FhPredicateContext,
): boolean {
  if (!predicate) {
    return true;
  }
  const entries = Object.entries(predicate);
  if (!entries.length) {
    return true;
  }
  return entries.every(([key, expected]) => evaluateEntry(key, expected, ctx));
}

function evaluateEntry(key: string, expected: string | string[], ctx: FhPredicateContext): boolean {
  if (key === FH_PREDICATE_COUNTRIES_KEY) {
    const accepted = Array.isArray(expected) ? expected : [expected];
    return accepted.some(country => equals(country, ctx.country));
  }

  const fieldId = FH_PREDICATE_KEY_ALIASES[key] ?? key;
  const controlName = ctx.byFieldId.get(fieldId);
  if (!controlName) {
    // Fail closed: a typo hides the field rather than over-rendering it.
    if (!warnedKeys.has(key)) {
      warnedKeys.add(key);
      console.warn(FH_WARN.UNKNOWN_PREDICATE_KEY, key);
    }
    return false;
  }

  return valueMatches(ctx.values[controlName], expected);
}

function valueMatches(actual: unknown, expected: string | string[]): boolean {
  const accepted = Array.isArray(expected) ? expected : [expected];
  // A multi-select holds an array; it matches when any selected value is accepted.
  if (Array.isArray(actual)) {
    return actual.some(value => accepted.some(candidate => equals(candidate, value)));
  }
  return accepted.some(candidate => equals(candidate, actual));
}

// Normalizes the comparison so that a config's "yes"/"no" still matches a control holding a real
// boolean (or the stringified "true"/"false" a BOOLEAN field rendered as a select hands back, see
// FH_BOOLEAN_OPTIONS), and so casing/whitespace differences between the config and the backend
// never matter.
function equals(expected: unknown, actual: unknown): boolean {
  return normalize(expected) === normalize(actual);
}

function normalize(value: unknown): string {
  if (value === true || value === FH_FLAG.TRUE) {
    return FH_YES_NO.YES;
  }
  if (value === false || value === FH_FLAG.FALSE) {
    return FH_YES_NO.NO;
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim().toLowerCase();
}

export function isSectionVisible(section: CompanyFormSection, ctx: FhPredicateContext): boolean {
  return evaluatePredicate(section.visibleWhen, ctx);
}

export function isFieldVisible(field: CompanyFieldDescriptor, ctx: FhPredicateContext): boolean {
  // A backend `visible: false` is absolute — visible_when can only narrow further, never widen.
  if (!field.visible) {
    return false;
  }
  if (!hasRole(field.visibleFor, ctx)) {
    return false;
  }
  return evaluatePredicate(field.visibleWhen, ctx);
}

export function isFieldRequired(field: CompanyFieldDescriptor, ctx: FhPredicateContext): boolean {
  // A hidden field is never required, otherwise the form could never become valid.
  if (!isFieldVisible(field, ctx)) {
    return false;
  }
  // NOTE: unlike visible_when, an ABSENT required_when means "not conditionally required".
  // evaluatePredicate returns true for a null predicate, so it must not be consulted here.
  return field.required || (!!field.requiredWhen && evaluatePredicate(field.requiredWhen, ctx));
}

export function isFieldEditable(field: CompanyFieldDescriptor, ctx: FhPredicateContext): boolean {
  // A SELECT resolved to exactly one option is auto-filled and locked read-only (PRD: "una sola
  // opción... se muestra read-only") — this overrides editableBy, it's not a role concern.
  if (field.singleOption) {
    return false;
  }
  // Tax ID is only editable until the company has an integration_settlement_id (PRD: "Editable
  // solo mientras no exista integration_settlement_id") — also not a role concern, and absolute
  // like singleOption above: once settled, no role can re-enable it.
  if (field.fieldId === FH_FIELD.TAX_ID && ctx.hasIntegrationSettlementId) {
    return false;
  }
  // Status/role whitelist from company-permissions.config.json (e.g. KAM in Verified).
  if (ctx.editableFieldIds && !ctx.editableFieldIds.includes(field.fieldId)) {
    return false;
  }
  return hasRole(field.editableBy, ctx);
}

// An empty role list means "everyone". Otherwise the user must hold one of the listed roles.
function hasRole(roles: string[], ctx: FhPredicateContext): boolean {
  if (!roles || !roles.length) {
    return true;
  }
  return roles.some(role => {
    if (role === FH_ROLE.BBR_ADMIN) {
      return ctx.isAdmin;
    }
    if (role === FH_ROLE.FINANCE_APPROVER) {
      return ctx.isFinanceApprover || ctx.isAdmin;
    }
    return false;
  });
}

// Exposed for tests — the warn-once cache is module state.
export function resetPredicateWarnings(): void {
  warnedKeys.clear();
}
